
import { useState, useEffect } from 'react';
import useIntersectionObserver from '@/hooks/use-intersection-observer';
import { getResponsiveImageProps } from '@/utils/imageOptimizer';

interface LazyImageProps {
  src: string;
  alt: string;
  title?: string;
  width?: number;
  height?: number;
  className?: string;
  placeholderSrc?: string;
  onLoad?: () => void;
}

const LazyImage = ({
  src,
  alt,
  title,
  width,
  height,
  className = '',
  placeholderSrc = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23cccccc"/%3E%3C/svg%3E',
  onLoad
}: LazyImageProps) => {
  const [ref, isVisible] = useIntersectionObserver<HTMLDivElement>();
  const [loaded, setLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(placeholderSrc);
  const responsiveProps = getResponsiveImageProps(src);

  useEffect(() => {
    if (isVisible && !loaded) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setLoaded(true);
        setCurrentSrc(src);
        if (onLoad) onLoad();
      };
    }
  }, [isVisible, loaded, onLoad, src]);

  return (
    <div ref={ref} style={{
      position: 'relative'
    }} className="">
      {!loaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" style={{
        aspectRatio: width && height ? `${width}/${height}` : 'auto'
      }}></div>}
      {<img
        src={currentSrc}
        alt={alt}
        title={title}
        width={width}
        height={height}
        className={`transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        {...responsiveProps}
      />}
    </div>
  );
};

export default LazyImage;
